import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationAPI } from '../services/api';
import { playNotificationChime, isNotificationMuted, setNotificationMuted } from '../utils/notificationSound';
import { Bell, Check, CheckCheck, X, FileText, CalendarCheck, CalendarX, DoorOpen, UserCheck, Megaphone, CreditCard, Car, Volume2, VolumeX, BarChart3 } from 'lucide-react';
import { Badge } from './ui/badge';
import { ThisWeekModal } from './ThisWeekModal';

const ICON_MAP = {
  invoice_created: { icon: FileText, color: 'text-amber-500', bg: 'bg-amber-50' },
  booking_approved: { icon: CalendarCheck, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  booking_rejected: { icon: CalendarX, color: 'text-red-500', bg: 'bg-red-50' },
  gate_pass_used: { icon: DoorOpen, color: 'text-gk-teal', bg: 'bg-gk-bg' },
  staff_checkin: { icon: UserCheck, color: 'text-gk-navy-light', bg: 'bg-slate-50' },
  notice_posted: { icon: Megaphone, color: 'text-blue-500', bg: 'bg-blue-50' },
  payment_confirmed: { icon: CreditCard, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  payment_received: { icon: CreditCard, color: 'text-green-600', bg: 'bg-green-50' },
  vehicle_approved: { icon: Car, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  vehicle_rejected: { icon: Car, color: 'text-red-500', bg: 'bg-red-50' },
  vehicle_approval_request: { icon: Car, color: 'text-amber-500', bg: 'bg-amber-50' },
  community_pulse: { icon: BarChart3, color: 'text-cyan-600', bg: 'bg-cyan-50' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    // Try parsing non-standard formats (e.g., "2026-04-15 21:22:00.000")
    const parsed = new Date(dateStr.replace(' ', 'T') + (dateStr.includes('+') || dateStr.endsWith('Z') ? '' : 'Z'));
    if (isNaN(parsed.getTime())) return dateStr.slice(0, 16);
    return formatIST(parsed, now);
  }
  return formatIST(date, now);
}

function formatIST(date, now) {
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'yesterday';
  return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}

// ─── Notification Item ──────────────────────────────────────────────

function NotificationItem({ notification, onMarkRead, onOpenItem }) {
  const iconInfo = ICON_MAP[notification.type] || { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-50' };
  const IconComponent = iconInfo.icon;

  const handleClick = () => {
    if (!notification.read) onMarkRead(notification.id);
    if (onOpenItem) onOpenItem(notification);
  };

  return (
    <div
      data-testid={`notification-item-${notification.id}`}
      className={`flex gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${
        !notification.read ? 'bg-gk-bg/40' : ''
      }`}
      onClick={handleClick}
    >
      <div className={`flex-shrink-0 w-9 h-9 rounded-full ${iconInfo.bg} flex items-center justify-center mt-0.5`}>
        <IconComponent className={`w-4 h-4 ${iconInfo.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${!notification.read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gk-teal mt-1.5" />
          )}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-slate-400">{timeAgo(notification.created_at)}</span>
          {notification.is_broadcast && (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-blue-200 text-blue-600">
              Community
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Notification Panel ─────────────────────────────────────────────

function NotificationPanel({ notifications, unreadCount, onMarkRead, onMarkAllRead, onOpenItem, onClose, soundMuted, onToggleMute }) {
  return (
    <div
      data-testid="notification-panel"
      className="fixed inset-x-0 top-0 sm:absolute sm:inset-x-auto sm:right-0 sm:top-12 w-full sm:w-[380px] h-full sm:h-auto max-h-full sm:max-h-[480px] bg-white sm:rounded-xl shadow-2xl border-0 sm:border sm:border-slate-200 z-50 overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
        <h3 className="font-semibold text-slate-900 text-sm">Notifications</h3>
        <div className="flex items-center gap-2">
          <button
            data-testid="toggle-notification-sound"
            onClick={onToggleMute}
            className={`p-1 rounded transition-colors ${soundMuted ? 'text-slate-400 hover:text-slate-600' : 'text-gk-teal hover:text-gk-teal-hover'}`}
            title={soundMuted ? 'Unmute notifications' : 'Mute notifications'}
          >
            {soundMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          {unreadCount > 0 && (
            <button
              data-testid="mark-all-read-button"
              onClick={onMarkAllRead}
              className="text-xs text-gk-teal hover:text-gk-teal-hover font-medium flex items-center gap-1"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Read all
            </button>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="overflow-y-auto max-h-[calc(100vh-52px)] sm:max-h-[400px]">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <Bell className="w-10 h-10 mb-2 opacity-30" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} onMarkRead={onMarkRead} onOpenItem={onOpenItem} />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main Bell ──────────────────────────────────────────────────────

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [showThisWeek, setShowThisWeek] = useState(false);
  const [soundMuted, setSoundMuted] = useState(isNotificationMuted());
  const panelRef = useRef(null);
  const prevUnreadRef = useRef(-1);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll();
      const newNotifs = res.data.notifications || [];
      const newUnread = res.data.unread_count || 0;

      if (prevUnreadRef.current >= 0 && newUnread > prevUnreadRef.current) {
        playNotificationChime();
      }
      prevUnreadRef.current = newUnread;

      setNotifications(newNotifs);
      setUnreadCount(newUnread);
    } catch {
      // network failure is expected when offline
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = useCallback(async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error('Mark read failed:', err); }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) { console.error('Mark all read failed:', err); }
  }, []);

  const handleToggleMute = useCallback(() => {
    const newVal = !soundMuted;
    setSoundMuted(newVal);
    setNotificationMuted(newVal);
  }, [soundMuted]);

  const handleOpenItem = useCallback((notification) => {
    if (notification?.type === 'community_pulse') {
      setIsOpen(false);
      setShowThisWeek(true);
    }
  }, []);

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-pulse"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          onMarkRead={handleMarkRead}
          onMarkAllRead={handleMarkAllRead}
          onOpenItem={handleOpenItem}
          onClose={() => setIsOpen(false)}
          soundMuted={soundMuted}
          onToggleMute={handleToggleMute}
        />
      )}

      <ThisWeekModal open={showThisWeek} onOpenChange={setShowThisWeek} />
    </div>
  );
}
